import { useState, useEffect } from "react"
import { getAccessToken } from "../../auth"
import Breadcrumbs from '../../components/Breadcrumbs';
import PageHeader from '../ui/PageHeader';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getUserProfile, updateUserProfile } from '../../api';

export default function Profile() {
  const [userEmail, setUserEmail] = useState("");
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    company_address_line1: '',
    company_address_line2: '',
    company_city: '',
    company_state: '',
    company_postal_code: '',
    company_country: '',
    vat_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  const crumbs = [
    { label: 'Dashboard', to: '/app/dashboard' },
    { label: 'Profile' }
  ];
  
  useEffect(() => {
    // Use an async IIFE to handle the async token fetching
    const fetchUserEmail = async () => {
      try {
        const accessToken = await getAccessToken(); // Await the async call
        if (accessToken && accessToken.sub) {
          setUserEmail(accessToken.sub); // Set email from the 'sub' claim
        } else {
          console.error("Access token or sub claim not found after fetching.");
          // Handle the case where token/sub is missing (e.g., redirect?)
        }
      } catch (error) {
        console.error("Error fetching access token:", error);
        // Handle error (e.g., redirect to login?)
      }
    };

    fetchUserEmail();
  }, []);

  useEffect(() => {
    // Fetch profile fields
    const fetchProfile = async () => {
      try {
        const p = await getUserProfile();
        setForm({
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          company_name: p.company_name || '',
          company_address_line1: p.company_address_line1 || '',
          company_address_line2: p.company_address_line2 || '',
          company_city: p.company_city || '',
          company_state: p.company_state || '',
          company_postal_code: p.company_postal_code || '',
          company_country: p.company_country || '',
          vat_id: p.vat_id || '',
        });
      } catch (e) {
        console.error('Failed to load profile', e);
      }
    };
    fetchProfile();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');
    try {
      // Send only filled or empty strings (backend will null out empty)
      await updateUserProfile(form);
      setSaveMessage('Profile updated');
    } catch (err) {
      console.error('Failed to update profile', err);
      setSaveMessage(err?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs crumbs={crumbs} />
      
      <PageHeader
        title="Profile"
        subtitle="Manage your account settings and preferences"
      />

      {/* Account Information */}
      <Card>
        <CardHeader
          title="Account information"
          subtitle="View your account details"
        />
        <CardContent>
          <form className="space-y-4" onSubmit={onSave}>
            <Input
              type="email"
              id="email"
              label="Email"
              value={userEmail}
              disabled={true}
              placeholder="Your email address"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                id="first_name"
                name="first_name"
                label="First name"
                value={form.first_name}
                onChange={onChange}
                placeholder="John"
              />
              <Input
                type="text"
                id="last_name"
                name="last_name"
                label="Last name"
                value={form.last_name}
                onChange={onChange}
                placeholder="Doe"
              />
            </div>
            <Input
              type="text"
              id="company_name"
              name="company_name"
              label="Company name"
              value={form.company_name}
              onChange={onChange}
              placeholder="Acme Inc."
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground">Billing address</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  id="company_address_line1"
                  name="company_address_line1"
                  placeholder="Address line 1"
                  value={form.company_address_line1}
                  onChange={onChange}
                />
                <Input
                  type="text"
                  id="company_address_line2"
                  name="company_address_line2"
                  placeholder="Address line 2 (optional)"
                  value={form.company_address_line2}
                  onChange={onChange}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  type="text"
                  id="company_city"
                  name="company_city"
                  placeholder="City"
                  value={form.company_city}
                  onChange={onChange}
                />
                <Input
                  type="text"
                  id="company_state"
                  name="company_state"
                  placeholder="State / Region"
                  value={form.company_state}
                  onChange={onChange}
                />
                <Input
                  type="text"
                  id="company_postal_code"
                  name="company_postal_code"
                  placeholder="Postal code"
                  value={form.company_postal_code}
                  onChange={onChange}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  type="text"
                  id="company_country"
                  name="company_country"
                  placeholder="Country (2-letter code, e.g., NL)"
                  value={form.company_country}
                  onChange={onChange}
                />
              </div>
            </div>
            <Input
              type="text"
              id="vat_id"
              name="vat_id"
              label="VAT ID"
              value={form.vat_id}
              onChange={onChange}
              placeholder="NL123456789B01"
            />
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
              {saveMessage && (<span className="text-sm text-muted-foreground">{saveMessage}</span>)}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card>
        <CardHeader
          title="Danger zone"
          subtitle="Irreversible and destructive actions"
        />
        <CardContent>
          <div className="rounded-xl border border-danger/20 bg-danger/5 p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground">Delete account</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This feature is not yet implemented. Please contact support to delete your account.
                </p>
                <div className="mt-4">
                  <Button
                    variant="danger"
                    disabled={true}
                  >
                    Delete account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

